<script lang="ts">
    import {enhance} from "$app/forms";
    import CardLayout from "$items/CardLayout.svelte";
    import type {Employee} from "$lib/types/employee";
    import InfoBox from "$items/InfoBox.svelte";
    import type {Writable} from "svelte/store";
    import type {EmployeeExtra} from "$lib/types/employee";
    import {getContext, onMount} from "svelte";
    import {Button, Hr, Toggle} from "flowbite-svelte";
    import {PenOutline} from "flowbite-svelte-icons";
    import {maritalStatus} from "$lib/options";
    import VerticalGrid from "$lib/components/VerticalGrid.svelte";
    import ApplyTrigger from "$items/employee/ApplyTrigger.svelte";
    import CardLayoutSubmitButton from "$items/employee/form/CardLayoutSubmitButton.svelte";
    import IncompleteDataWarning from "$lib/components/IncompleteDataWarning.svelte";


    let super_edit: Writable<boolean> = getContext('super_edit');


    export let loading = false;
    export let readOnly = false;
    let employee: Writable<Employee> = getContext('employee');

    let display = false;


    const employee_extra: Writable<EmployeeExtra> = getContext('employee_extra');
    if ($employee && !$employee?.healthInsurance) {
        $employee.healthInsurance = {}
    }

    $: complete = $employee.healthInsurance?.insuranceName && $employee.healthInsurance?.insuranceNumber
        && $employee?.cv?.taxationClass && $employee?.cv?.religiousAffiliation

    onMount(() => {
        if(typeof $employee.healthInsurance.isPublic === 'undefined') {
            $employee.healthInsurance.isPublic = false
        }
    })
</script>

<CardLayout title="Krankenversicherung (KV) / Lohnsteuer" bind:display accordion>
    <svelte:fragment slot="card-header">
        <IncompleteDataWarning {complete}/>
        {#if !readOnly}
            <Button color="yellow" class="!p-2" pill outline on:click={() => $super_edit = !$super_edit}>
                <PenOutline class="w-4 h-4"/>
            </Button>
        {/if}
    </svelte:fragment>
    <form method="post" class="px-2 py-3"
          class:opacity-20={loading}
          use:enhance={() => {loading=true; return ({ update }) => {loading=false; update({ reset: false })}}}
          action="?/health_insurance">
        {#if $employee.healthInsurance}
        <VerticalGrid>

            <InfoBox title="Gesetzlich versichert" value={$employee?.healthInsurance?.isPublic ? 'Ja' : 'Nein'} let:value >
                <Toggle
                        value={$employee.healthInsurance.isPublic}
                        disabled={!$super_edit}
                        name="isPublic"
                        bind:checked={$employee.healthInsurance.isPublic}/>
            </InfoBox>
            <InfoBox title="Versicherungsnummer" value={$employee.healthInsurance?.insuranceNumber || ''}
                     name="insuranceNumber"/>

            <svelte:fragment slot="right">
                <InfoBox
                        title="Name {$employee.healthInsurance.isPublic ? 'gesetzliche' : 'private'} KV"
                        value={$employee.healthInsurance?.insuranceName || ''}
                        name="insuranceName"
                        items={$employee_extra.health_insurances?.map((n) => ({...n, name: n.value})).sort((a, b) => a.name.localeCompare(b.name))}
                        type={$employee.healthInsurance.isPublic ? "select" : "text"}
                />
                {#if !$employee.healthInsurance.isPublic}
                    <InfoBox title="Vorherige ges. Versicherung" value={$employee.healthInsurance?.previouslyInsuredBy || ''}
                             items={[{name: 'Keine', value: ''},...$employee_extra.health_insurances?.map((n) => ({...n, name: n.value})).sort((a, b) => a.name.localeCompare(b.name))]}
                             type="select"
                             name="previouslyInsuredBy"/>
                {/if}


            </svelte:fragment>

        </VerticalGrid>
        {/if}
        <Hr/>
        <VerticalGrid>
            <InfoBox title="SV-Nummer" value={$employee.cv.socialSecurityNumber || ''}
                     pattern={"[0-9]{8}[A-Z][0-9]{3}"}
                     name="socialSecurityNumber"/>
            <InfoBox title="Steuerklasse" name="taxationClass" type="number" value={$employee.cv.taxationClass || ''} />
            <InfoBox title="Konfession"
                     value={$employee.cv.religiousAffiliation || ''} name="religiousAffiliation"
                     items={$employee_extra.religious_denoms?.map((n) => ({...n, name: n.value}))}
                     type="select">
            </InfoBox>
            <svelte:fragment slot="right">
                <InfoBox title="Steuer ID" value={$employee.taxId || '-'}
                         name="taxId"/>

                <InfoBox title="Kinderfreibetrag" name="childAllowance" type="number" step="0.5" value={$employee.cv.childAllowance || 0} />

                <InfoBox title="Familienstand"
                         value={$employee.cv?.familyStatus || '-'} name="familyStatus"
                         items={Object.entries(maritalStatus).map(([key, value]) => ({value:key, name:value}))}
                         type="select">
                </InfoBox>
            </svelte:fragment>
        </VerticalGrid>
        <CardLayoutSubmitButton/>
        <ApplyTrigger/>
    </form>
</CardLayout>

